import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { AppHeader } from "../../components/AppHeader";
import { Loading } from "../../components/Loading";
import { JobCard } from "../../components/JobCard";
import { Plus, BarChart3, CheckCircle2, Layers } from "lucide-react";
import { ProductTypeDonut } from "../../components/ProductTypeDonut";
import { KpiDetailModal } from "../../components/KpiDetailModal";

function Stat({ value, label, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
      <p className={`tnum text-4xl font-black ${color}`}>{value}</p>
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mt-1">{label}</p>
    </div>
  );
}

export default function OwnerDashboard() {
  const [data, setData] = useState(null);
  const [ptReport, setPtReport] = useState(null);
  const [modal, setModal] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/owner").then((r) => setData(r.data)).catch(() => {});
    const now = new Date();
    api.get("/reports/product-types", { params: { year: now.getFullYear(), month: now.getMonth() + 1 } })
      .then((r) => setPtReport(r.data)).catch(() => {});
  }, []);

  if (!data) return (<><AppHeader title="Copper Craft" subtitle="Owner" /><Loading /></>);

  const M = data.month;
  const P = data.production || {};
  const nowD = new Date();
  const curY = nowD.getFullYear();
  const curM = nowD.getMonth() + 1;
  const open = (kind, title, metric = "count", metricLabel = "jobs") =>
    setModal({ kind, title, metric, metricLabel });
  return (
    <>
      <AppHeader title="Copper Craft" subtitle="Owner Dashboard" />
      <div className="px-4 lg:px-8 py-4 lg:py-6 space-y-6">
        {/* Quick actions */}
        <div className="grid grid-cols-3 lg:max-w-2xl gap-3">
          <button data-testid="create-job-open-button" onClick={() => navigate("/owner/create")}
            className="flex flex-col items-center gap-1.5 copper-gradient text-white rounded-2xl py-4 shadow-md active:scale-95 transition-transform">
            <Plus className="h-6 w-6" strokeWidth={2.5} />
            <span className="text-xs font-bold">Create Job</span>
          </button>
          <button onClick={() => navigate("/owner/jobs?filter=READY FOR OWNER CHECK")}
            className="flex flex-col items-center gap-1.5 bg-white border border-slate-200 rounded-2xl py-4 active:scale-95 transition-transform">
            <CheckCircle2 className="h-6 w-6 text-indigo-600" strokeWidth={2.5} />
            <span className="text-xs font-bold text-slate-700">Check Ready</span>
          </button>
          <button onClick={() => navigate("/owner/reports")}
            className="flex flex-col items-center gap-1.5 bg-white border border-slate-200 rounded-2xl py-4 active:scale-95 transition-transform">
            <BarChart3 className="h-6 w-6 text-[#9A462B]" strokeWidth={2.5} />
            <span className="text-xs font-bold text-slate-700">Monthly</span>
          </button>
        </div>

        {/* TODAY */}
        <section>
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">Today</h2>
          <div className="grid grid-cols-3 gap-3">
            <Stat value={data.ready_for_check} label="Ready for Check" color="text-indigo-600" />
            <Stat value={data.active_jobs} label="Active Jobs" color="text-slate-900" />
            <Stat value={data.late_jobs} label="Late Jobs" color="text-red-600" />
          </div>
        </section>

        {/* Quality check required */}
        {data.ready_jobs.length > 0 && (
          <section>
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-indigo-700 mb-3 flex items-center gap-2">
              🔍 Quality Check Required
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {data.ready_jobs.map((j) => <JobCard key={j.id} job={j} basePath="/owner/jobs" />)}
            </div>
          </section>
        )}

        {/* THIS MONTH */}
        <section>
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">This Month</h2>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-[11px] text-slate-400 mb-3 italic">Tap any number to see the jobs behind it.</p>
            <div className="grid grid-cols-3 gap-3 text-center mb-4">
              <button data-testid="dash-kpi-completed" onClick={() => open("completed", "Completed This Month", "count", "jobs")}
                className="active:scale-[0.97] transition-transform group focus:outline-none">
                <p className="tnum text-3xl font-black text-slate-900 group-hover:underline underline-offset-4 decoration-dotted">{M.completed}</p>
                <p className="text-[10px] font-bold uppercase text-slate-400">Completed</p>
              </button>
              <button data-testid="dash-kpi-ontime" onClick={() => open("ontime", "On-Time Jobs This Month", "count", "on-time jobs")}
                className="active:scale-[0.97] transition-transform group focus:outline-none">
                <p className="tnum text-3xl font-black text-emerald-600 group-hover:underline underline-offset-4 decoration-dotted">{M.on_time}</p>
                <p className="text-[10px] font-bold uppercase text-slate-400">On Time</p>
              </button>
              <button data-testid="dash-kpi-breached" onClick={() => open("breached", "Breached Jobs This Month", "count", "breached jobs")}
                className="active:scale-[0.97] transition-transform group focus:outline-none">
                <p className="tnum text-3xl font-black text-red-600 group-hover:underline underline-offset-4 decoration-dotted">{M.breached}</p>
                <p className="text-[10px] font-bold uppercase text-slate-400">Breached</p>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="dash-kpi-ontime-pct" onClick={() => open("ontime", "On-Time Jobs This Month", "count", "on-time jobs")}
                className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center active:scale-[0.98] transition-transform">
                <p className="tnum text-2xl font-black text-emerald-700">{M.on_time_pct}%</p>
                <p className="text-[10px] font-bold uppercase text-emerald-600">On-Time</p>
              </button>
              <button data-testid="dash-kpi-breach-pct" onClick={() => open("breached", "Breached Jobs This Month", "count", "breached jobs")}
                className="rounded-xl bg-red-50 border border-red-200 p-3 text-center active:scale-[0.98] transition-transform">
                <p className="tnum text-2xl font-black text-red-700">{M.breach_pct}%</p>
                <p className="text-[10px] font-bold uppercase text-red-600">Breach</p>
              </button>
            </div>
          </div>
        </section>

        {/* MONTHLY PRODUCTION */}
        <section>
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">Monthly Production</h2>
          <div data-testid="monthly-production-summary" className="bg-white rounded-2xl border border-slate-200 p-5 mb-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <button data-testid="dash-kpi-created" onClick={() => open("created", "Jobs Created This Month", "count", "jobs created")}
                className="active:scale-[0.97] transition-transform group focus:outline-none">
                <p className="tnum text-2xl font-black text-slate-900 group-hover:underline underline-offset-4 decoration-dotted">{P.jobs_created ?? 0}</p>
                <p className="text-[10px] font-bold uppercase text-slate-400 mt-0.5">Jobs Created</p>
              </button>
              <button data-testid="dash-kpi-created-qty" onClick={() => open("created", "Created Quantity This Month", "qty", "pcs created")}
                className="active:scale-[0.97] transition-transform group focus:outline-none">
                <p className="tnum text-2xl font-black text-slate-900 group-hover:underline underline-offset-4 decoration-dotted">{(P.created_qty ?? 0).toLocaleString()}</p>
                <p className="text-[10px] font-bold uppercase text-slate-400 mt-0.5">Created Qty</p>
              </button>
              <button data-testid="dash-kpi-received-qty" onClick={() => open("received", "Received Quantity This Month", "received", "pcs received")}
                className="active:scale-[0.97] transition-transform group focus:outline-none">
                <p className="tnum text-2xl font-black text-emerald-700 group-hover:underline underline-offset-4 decoration-dotted">{(P.received_qty ?? 0).toLocaleString()}</p>
                <p className="text-[10px] font-bold uppercase text-emerald-600 mt-0.5">Received Qty</p>
              </button>
            </div>
          </div>
          <ProductTypeDonut data={ptReport} testid="owner-product-type-donut" />
        </section>

        {/* CURRENT JOBS */}
        <section>
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
            <Layers className="h-4 w-4" /> Current Jobs
          </h2>
          {data.current_jobs.length === 0 ? (
            <p className="text-slate-400 text-sm py-4 text-center">No active jobs right now.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {data.current_jobs.map((j) => <JobCard key={j.id} job={j} basePath="/owner/jobs" />)}
            </div>
          )}
        </section>
      </div>

      <KpiDetailModal open={!!modal} onClose={() => setModal(null)}
        kind={modal?.kind} year={curY} month={curM}
        title={modal?.title} metric={modal?.metric} metricLabel={modal?.metricLabel} />
    </>
  );
}
